import React, { useState, useEffect } from 'react';
import './Projects.css';

const SUBSYSTEMS = [
  { key: 'workshop', label: 'UGP Workshop' },
  { key: 'drumball', label: 'Drumball Device' },
  { key: 'griotquest', label: 'Griotquest Kits' },
  { key: 'activity_manager', label: 'Activity Manager' },
  { key: 'mobile_game', label: 'Mobile Game' },
];

const AIMS = [
  { key: 'research', label: 'Research' },
  { key: 'prototyping', label: 'Prototyping' },
  { key: 'curriculum_design', label: 'Curriculum Design' },
  { key: 'advisory_communication', label: 'Advisory/Comms' },
  { key: 'teacher_pd', label: 'Teacher PD' },
  { key: 'family_workshops', label: 'Family Workshops' },
];

const SOURCE_TOOLS = ['manual', 'trello', 'figma', 'discord', 'drive', 'onedrive'];

const emptyForm = { title: '', subsystem: 'workshop', aim: 'research', sourceTool: 'manual', link: '', notes: '' };

export default function Projects() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState({ subsystem: null, aim: null });
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  async function fetchTasks() {
    try {
      const res = await fetch('/api/tasks');
      const data = await res.json();
      if (data.success) setTasks(data.tasks || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setTasks((prev) => [data.task, ...prev]);
        setForm(emptyForm);
        setModalOpen(false);
      }
    } catch (err) {
      console.error('Error creating task:', err);
    }
  }

  function countFor(subsystem, aim) {
    return tasks.filter((t) => t.subsystem === subsystem && t.aim === aim).length;
  }

  function toggleFilter(subsystem, aim) {
    const active = filter.subsystem === subsystem && filter.aim === aim;
    setFilter(active ? { subsystem: null, aim: null } : { subsystem, aim });
  }

  const filtered = tasks.filter(
    (t) => (!filter.subsystem || t.subsystem === filter.subsystem) && (!filter.aim || t.aim === filter.aim)
  );

  if (loading) {
    return <div className="proj-loading"><div className="proj-spinner" /></div>;
  }

  return (
    <div className="proj-view">
      <div className="proj-header">
        <h1>Projects</h1>
        <button className="mihu-btn-primary-lg" onClick={() => setModalOpen(true)}>+ Add task</button>
      </div>

      <div className="proj-matrix-wrap">
        <table className="proj-matrix">
          <thead>
            <tr>
              <th>Subsystem \ Aim</th>
              {AIMS.map((a) => <th key={a.key}>{a.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {SUBSYSTEMS.map((s) => (
              <tr key={s.key}>
                <td className="proj-matrix-row-label">{s.label}</td>
                {AIMS.map((a) => {
                  const count = countFor(s.key, a.key);
                  const active = filter.subsystem === s.key && filter.aim === a.key;
                  return (
                    <td
                      key={a.key}
                      className={"proj-matrix-cell" + (active ? " active" : "")}
                      onClick={() => toggleFilter(s.key, a.key)}
                    >
                      {count > 0 ? count : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="proj-tasks-label">Tasks {filter.subsystem ? '— filtered' : '(all)'}</h2>

      <div className="proj-tasks-wrap">
        <table className="proj-tasks-table">
          <thead>
            <tr>
              <th>Title</th><th>Subsystem</th><th>Aim</th><th>Source</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id}>
                <td>{t.link ? <a href={t.link} target="_blank" rel="noreferrer">{t.title}</a> : t.title}</td>
                <td>{SUBSYSTEMS.find((s) => s.key === t.subsystem)?.label}</td>
                <td>{AIMS.find((a) => a.key === t.aim)?.label}</td>
                <td><span className="proj-chip">{t.sourceTool}</span></td>
                <td>{t.status}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="proj-empty">No tasks match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="proj-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="proj-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add task</h3>

            <div className="proj-modal-field">
              <label>Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>

            <div className="proj-modal-row">
              <div className="proj-modal-field">
                <label>Subsystem</label>
                <select value={form.subsystem} onChange={(e) => setForm({ ...form, subsystem: e.target.value })}>
                  {SUBSYSTEMS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div className="proj-modal-field">
                <label>Aim</label>
                <select value={form.aim} onChange={(e) => setForm({ ...form, aim: e.target.value })}>
                  {AIMS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
              </div>
            </div>

            <div className="proj-modal-field">
              <label>Source tool</label>
              <select value={form.sourceTool} onChange={(e) => setForm({ ...form, sourceTool: e.target.value })}>
                {SOURCE_TOOLS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="proj-modal-field">
              <label>Link</label>
              <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
            </div>

            <div className="proj-modal-field">
              <label>Notes</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div className="proj-modal-actions">
              <button className="proj-modal-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="mihu-btn-primary-lg" onClick={handleCreate} disabled={!form.title}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
